// Lender Service - Firestore CRUD operations for lenders
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Lender } from '../types';

const LENDERS_COLLECTION = 'lenders';

/**
 * Create a new lender in Firestore
 */
export const createLender = async (lenderData: Omit<Lender, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, LENDERS_COLLECTION), {
      ...lenderData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating lender:', error);
    throw new Error('Failed to create lender');
  }
};

/**
 * Get all lenders from Firestore
 */
export const getAllLenders = async (): Promise<Lender[]> => {
  try {
    const querySnapshot = await getDocs(
      query(collection(db, LENDERS_COLLECTION), orderBy('fullName'))
    );
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Lender[];
  } catch (error) {
    console.error('Error getting lenders:', error);
    throw new Error('Failed to fetch lenders');
  }
};

/**
 * Get a single lender by ID
 */
export const getLenderById = async (lenderId: string): Promise<Lender | null> => {
  try {
    const docRef = doc(db, LENDERS_COLLECTION, lenderId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as Lender;
    }
    return null;
  } catch (error) {
    console.error('Error getting lender:', error);
    throw new Error('Failed to fetch lender');
  }
};

/**
 * Update a lender in Firestore
 */
export const updateLender = async (
  lenderId: string,
  updates: Partial<Omit<Lender, 'id'>>
): Promise<void> => {
  try {
    const docRef = doc(db, LENDERS_COLLECTION, lenderId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating lender:', error);
    throw new Error('Failed to update lender');
  }
};

/**
 * Delete a lender from Firestore
 */
export const deleteLender = async (lenderId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, LENDERS_COLLECTION, lenderId));
  } catch (error) {
    console.error('Error deleting lender:', error);
    throw new Error('Failed to delete lender');
  }
};

/**
 * Search lenders by name or email
 */
export const searchLenders = async (searchTerm: string): Promise<Lender[]> => {
  try {
    const lenders = await getAllLenders();
    const term = searchTerm.toLowerCase();
    
    return lenders.filter(lender =>
      lender.fullName.toLowerCase().includes(term) ||
      lender.email.toLowerCase().includes(term)
    );
  } catch (error) {
    console.error('Error searching lenders:', error);
    throw new Error('Failed to search lenders');
  }
};

/**
 * Subscribe to real-time lender updates
 */
export const subscribeLenders = (
  callback: (lenders: Lender[]) => void,
  onError?: (error: Error) => void
) => {
  const q = query(collection(db, LENDERS_COLLECTION), orderBy('fullName'));
  
  return onSnapshot(
    q,
    (snapshot) => {
      const lenders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Lender[];
      callback(lenders);
    },
    (error) => {
      console.error('Error in lender subscription:', error);
      if (onError) {
        onError(new Error('Failed to subscribe to lenders'));
      }
    }
  );
};

/**
 * Get lenders with active series
 * Note: This queries series separately to check which lenders have series
 */
export const getActiveLenders = async (): Promise<Lender[]> => {
  try {
    // For now, just return all lenders
    // In production, you'd query series collection to find lenders with active series
    const lenders = await getAllLenders();
    return lenders;
  } catch (error) {
    console.error('Error getting active lenders:', error);
    throw new Error('Failed to fetch active lenders');
  }
};
